import { z } from 'zod';

import { IsoDateTimeSchema } from './common.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Marketing reports - "Query Profit and Spend".
 *
 * Two kinds of cost sit behind the report:
 *
 *   - Cost heads        - a master (`MarketingCostHead`) of every cost the
 *                         report puts against a month other than ad spend: the
 *                         management retainer, WhatsApp, supplier APIs, another
 *                         channel's ad account, whatever ops pay for. Each head's
 *                         `mode` says how its monthly figure is found (entered
 *                         once and carried forward, entered per month, or
 *                         derived as count x rate) and the report renders one
 *                         Spend row per active head. The operator picks which
 *                         heads a report includes (`costs`); only those count.
 *   - All Account Spend - total ad spend for the month, DERIVED as the sum of
 *                         the month's adset rows (see the adset section below).
 *                         Never a head: it is measured per adset, has no rate,
 *                         and is never typed at month grain.
 *
 * Ad spend and every included REQUIRED head must resolve before a month's
 * profit renders - profit per query against a missing cost side would be wrong,
 * not merely incomplete - so the report is gated on `isComplete`. An optional
 * head with no figure simply contributes nothing.
 */

const IsoDateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
const IsoMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM');

/** Upper bound on either cost field - guards against a mistyped extra digit. */
export const MARKETING_SPEND_MAX = 1_000_000_000;

/** Query Type codes the report splits on (`query_types.code`). */
export const MarketingLeadTypeSchema = z.enum(['inorganic', 'organic']);
export type MarketingLeadType = z.infer<typeof MarketingLeadTypeSchema>;

export const MarketingSpendRangeQuerySchema = z.object({
  /** Inclusive IST calendar date (YYYY-MM-DD); the response covers every month it touches. */
  from: IsoDateOnly,
  /** Inclusive IST calendar date (YYYY-MM-DD). */
  to: IsoDateOnly,
  /**
   * Optional explicit month set, comma-separated (`2026-05,2026-07`).
   *
   * The report works in whole months, and the admin filter is a month
   * MULTI-select, so the chosen months can have HOLES - May and July without
   * June. `from`/`to` can only express a contiguous span, so when this is
   * present it is authoritative and the months not listed are excluded from
   * every aggregate. Absent (the pre-existing callers, incl. MCP), the range
   * expands to every month it touches exactly as before.
   *
   * Unparseable entries are dropped rather than rejected - a stale link with
   * one bad month should still render the months it names.
   */
  months: z
    .string()
    .trim()
    .optional()
    .transform((raw) => {
      if (!raw) return undefined;
      const parts = [
        ...new Set(
          raw
            .split(',')
            .map((part) => part.trim())
            .filter((part) => IsoMonth.safeParse(part).success),
        ),
      ].sort();
      return parts.length > 0 ? parts : undefined;
    }),
  /**
   * Optional cost-head codes to INCLUDE in the month's cost side, comma-separated
   * (`management,whatsapp`). Only the heads named are resolved and summed into
   * Total Spend; the rest are left out of every cost-derived figure (CPQ, ROI,
   * RoAS, CAC, Pure Profit) and out of the completeness gate. Absent = every
   * active head, which is what the pre-existing callers (incl. MCP) get.
   *
   * Ad spend (the adset roll-up) is not a head and is always included.
   */
  costs: z
    .string()
    .trim()
    .optional()
    .transform((raw) => {
      if (!raw) return undefined;
      const parts = [
        ...new Set(
          raw
            .split(',')
            .map((part) => part.trim())
            .filter((part) => MasterCodeSchema.safeParse(part).success),
        ),
      ].sort();
      return parts.length > 0 ? parts : undefined;
    }),
  /**
   * Optional lead type - the `code` of the lead's Query Type master. Every
   * lead aggregate counts only leads of that type. The report is read as two
   * sections: Inorganic (paid - where ad spend and every cost head belong) and
   * Organic (came to us on their own - no cost side at all, so the spend-based
   * figures are null). Absent = every lead, which is what the pre-existing
   * callers (incl. MCP) get.
   */
  leadType: MarketingLeadTypeSchema.optional(),
});
export type MarketingSpendRangeQuery = z.infer<typeof MarketingSpendRangeQuerySchema>;

/* -------------------------------------------------------------------------- */
/*                               Cost heads master                             */
/* -------------------------------------------------------------------------- */

/**
 * A cost the report can put against a month. The generalisation of the old
 * single management-cost input: every Spend row of the source sheet other than
 * ad spend is one of these, and the report renders one row per active head.
 *
 * `mode` says how a month's figure is found:
 *   - CARRY_FORWARD - entered once with an effective month, applies to every
 *                     later month until superseded (a retainer)
 *   - PER_MONTH     - entered for each month; a month with no entry has none
 *   - DERIVED       - count(driver events in the month) x rate; a saved entry
 *                     for the month overrides the computed figure
 *
 * Ad spend is NOT a head - it is measured per adset and rolls up on its own.
 */
export const MarketingCostModeSchema = z.enum(['CARRY_FORWARD', 'PER_MONTH', 'DERIVED']);
export type MarketingCostMode = z.infer<typeof MarketingCostModeSchema>;

/** What a DERIVED head counts - each is a query over data the system keeps. */
export const MarketingCostDriverSchema = z.enum([
  'WHATSAPP_MESSAGES',
  'WHATSAPP_CALLS',
  'TBO_API_CALLS',
]);
export type MarketingCostDriver = z.infer<typeof MarketingCostDriverSchema>;

/** Display unit for each driver's count, e.g. "1,240 messages". */
export const MARKETING_COST_DRIVER_UNIT: Record<MarketingCostDriver, string> = {
  WHATSAPP_MESSAGES: 'messages',
  WHATSAPP_CALLS: 'calls',
  TBO_API_CALLS: 'API calls',
};

export const MarketingCostHeadSchema = z.object({
  id: z.string().uuid(),
  code: MasterCodeSchema,
  name: z.string(),
  description: z.string().nullable(),
  mode: MarketingCostModeSchema,
  /** Set only for DERIVED heads. */
  driver: MarketingCostDriverSchema.nullable(),
  /** Rupees per counted unit; set only for DERIVED heads. */
  rate: z.number().nonnegative().nullable(),
  /**
   * Lead source this cost belongs to. Null = an overhead that belongs to no
   * single channel and only ever counts against the overall figures.
   */
  querySourceId: z.string().uuid().nullable(),
  querySourceName: z.string().nullable(),
  /** A missing figure locks the month (true) or contributes nothing (false). */
  isRequired: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type MarketingCostHead = z.infer<typeof MarketingCostHeadSchema>;

export const MarketingCostHeadListResponseSchema = z.object({
  data: z.array(MarketingCostHeadSchema),
});
export type MarketingCostHeadListResponse = z.infer<typeof MarketingCostHeadListResponseSchema>;

const CostHeadCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  mode: MarketingCostModeSchema,
  driver: MarketingCostDriverSchema.nullable().optional(),
  rate: z.number().nonnegative().max(MARKETING_SPEND_MAX).nullable().optional(),
  querySourceId: z.string().uuid().nullable().optional(),
  isRequired: z.boolean().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});

/** A DERIVED head needs both a driver and a rate; the other modes take neither. */
const derivedFieldsConsistent = (
  value: { mode?: MarketingCostMode; driver?: unknown; rate?: unknown },
  ctx: z.RefinementCtx,
): void => {
  if (value.mode === undefined) return;
  const hasDriver = value.driver !== null && value.driver !== undefined;
  const hasRate = value.rate !== null && value.rate !== undefined;
  if (value.mode === 'DERIVED' && !(hasDriver && hasRate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A derived cost needs both a driver and a rate.',
      path: ['mode'],
    });
  }
  if (value.mode !== 'DERIVED' && (hasDriver || hasRate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Only a derived cost takes a driver and a rate.',
      path: ['mode'],
    });
  }
};

export const CreateMarketingCostHeadRequestSchema =
  CostHeadCoreFieldsSchema.superRefine(derivedFieldsConsistent);
export type CreateMarketingCostHeadRequest = z.infer<typeof CreateMarketingCostHeadRequestSchema>;

export const UpdateMarketingCostHeadRequestSchema =
  CostHeadCoreFieldsSchema.partial().superRefine(derivedFieldsConsistent);
export type UpdateMarketingCostHeadRequest = z.infer<typeof UpdateMarketingCostHeadRequestSchema>;

/**
 * Save one month's figure for a head. The amount REPLACES what is stored for
 * that head and month. For a CARRY_FORWARD head this is the rate effective
 * from `month` onward; for PER_MONTH it is that month's bill; for DERIVED it
 * is an override of the computed figure.
 */
export const UpsertMarketingCostEntryRequestSchema = z.object({
  /** Cost head code, e.g. `management`. */
  code: MasterCodeSchema,
  month: IsoMonth,
  amount: z.number().nonnegative().max(MARKETING_SPEND_MAX),
  notes: z.string().trim().max(500).nullable().optional(),
});
export type UpsertMarketingCostEntryRequest = z.infer<typeof UpsertMarketingCostEntryRequestSchema>;

/** Remove one month's entry (the head keeps every other month). */
export const DeleteMarketingCostEntryRequestSchema = z.object({
  code: MasterCodeSchema,
  month: IsoMonth,
});
export type DeleteMarketingCostEntryRequest = z.infer<typeof DeleteMarketingCostEntryRequestSchema>;

/**
 * Where a month's figure for a head came from - what the UI says next to it.
 *   entered  - a PER_MONTH entry for exactly this month
 *   carried  - a CARRY_FORWARD entry from an earlier month still in force
 *   derived  - count x rate
 *   override - a saved entry replacing a DERIVED figure
 *   missing  - nothing resolves; `amount` is null
 */
export const MarketingCostSourceSchema = z.enum([
  'entered',
  'carried',
  'derived',
  'override',
  'missing',
]);
export type MarketingCostSource = z.infer<typeof MarketingCostSourceSchema>;

/** One head's figure for one month, as the report resolved it. */
export const MarketingResolvedCostSchema = z.object({
  code: MasterCodeSchema,
  name: z.string(),
  mode: MarketingCostModeSchema,
  /** DERIVED heads: what `units` counts. */
  driver: MarketingCostDriverSchema.nullable(),
  querySourceId: z.string().uuid().nullable(),
  isRequired: z.boolean(),
  amount: z.number().nonnegative().nullable(),
  source: MarketingCostSourceSchema,
  /** CARRY_FORWARD: the month the figure in force was entered for. */
  effectiveFrom: IsoMonth.nullable(),
  /** DERIVED: how many driver events the month had, and the rate applied. */
  units: z.number().int().nonnegative().nullable(),
  rate: z.number().nonnegative().nullable(),
});
export type MarketingResolvedCost = z.infer<typeof MarketingResolvedCostSchema>;

/** One entry in the management-cost rate history. */
export const ManagementCostRateSchema = z.object({
  /** YYYY-MM this rate takes effect from; it applies to every later month too. */
  effectiveFrom: IsoMonth,
  /** Display label, e.g. "May 2026". */
  effectiveFromLabel: z.string(),
  amount: z.number().nonnegative(),
});
export type ManagementCostRate = z.infer<typeof ManagementCostRateSchema>;

export const MarketingSpendMonthSchema = z.object({
  /** YYYY-MM of the covered IST calendar month. */
  month: IsoMonth,
  /** Display label, e.g. "May 2026". */
  label: z.string(),
  /**
   * Resolved from the rate history - the latest rate effective on or before
   * this month. Null only when no rate covers the month (i.e. the month
   * predates the earliest rate), which locks the report.
   */
  managementCost: z.number().nonnegative().nullable(),
  /**
   * DERIVED, never entered: the month's total ad spend, being the sum of its
   * adset rows. Null when no adset spend has been recorded for the month, which
   * is what keeps the report locked rather than claiming a spend of zero.
   *
   * This used to be a figure the operator typed once a month, then a per-adset
   * manual entry. Spend is now pulled automatically from the Meta ads sync, one
   * row per adset per month, and rolls up (adset -> campaign -> month), so this
   * is the top of that roll-up rather than an input.
   */
  allAccountSpend: z.number().nonnegative().nullable(),
  /** How many adsets carry a figure for this month. 0 means nothing entered yet. */
  adsetCount: z.number().int().nonnegative(),
  /**
   * The most recent `asOfDate` across the month's adset rows (YYYY-MM-DD), or
   * null when none. Month-to-date figures are only as current as the day they
   * were read off Meta, so the report says which day that was instead of
   * implying the number is live.
   */
  spendAsOf: z.string().nullable(),
  /**
   * Every INCLUDED cost head resolved for this month, in master sort order.
   * `managementCost` above is the `management` head's amount, kept for callers
   * that predate the master.
   */
  costs: z.array(MarketingResolvedCostSchema),
  /** Sum of the resolved head amounts (missing heads contribute nothing). */
  headsTotal: z.number().nonnegative().nullable(),
  /** Ad spend + every resolved head. Null while the month is not complete. */
  totalSpend: z.number().nonnegative().nullable(),
  /** Codes of REQUIRED heads that did not resolve - what is locking the month. */
  missingRequired: z.array(MasterCodeSchema),
  /** Ad spend and every required head resolve - this month can feed the report. */
  isComplete: z.boolean(),
});
export type MarketingSpendMonth = z.infer<typeof MarketingSpendMonthSchema>;

export const MarketingSpendResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  /**
   * Rate in force for the latest month of the range - what the "current"
   * management cost is. Null when no rate has ever been recorded.
   */
  currentManagementCost: ManagementCostRateSchema.nullable(),
  /** Full rate history, newest first, so the UI can show when the price changed. */
  managementCostHistory: z.array(ManagementCostRateSchema),
  /** Every active cost head, in master order - the rows the cost card renders. */
  heads: z.array(MarketingCostHeadSchema),
  /** Codes of the heads this response resolved (the `costs` filter, or all active). */
  includedCosts: z.array(MasterCodeSchema),
  /** One entry per IST month the requested range touches, oldest first. */
  months: z.array(MarketingSpendMonthSchema),
  /** True when EVERY month in range is complete - the dashboard unlocks. */
  isComplete: z.boolean(),
  generatedAt: IsoDateTimeSchema,
});
export type MarketingSpendResponse = z.infer<typeof MarketingSpendResponseSchema>;

/**
 * Record the monthly management-cost retainer from `effectiveFrom` onward.
 * Re-sending the same month corrects that rate in place; a later month starts
 * a new rate and leaves earlier months on the old one.
 */
export const SetManagementCostRequestSchema = z.object({
  effectiveFrom: IsoMonth,
  amount: z.number().nonnegative().max(MARKETING_SPEND_MAX),
});
export type SetManagementCostRequest = z.infer<typeof SetManagementCostRequestSchema>;

/* -------------------------------------------------------------------------- */
/*                          Adset-level spend capture                          */
/* -------------------------------------------------------------------------- */

/**
 * Ad spend is captured at ADSET level, because that is the grain Meta prices at
 * and - critically - an adset targets exactly ONE destination. That makes
 * per-destination cost a measurement rather than an allocation: the previous
 * design held a single monthly total, and splitting it pro-rata would have given
 * every destination an identical Cost Per Query.
 *
 * Nothing above an adset is ever typed. Adsets sum into campaigns, campaigns
 * into the month, and the month total feeds the profit table exactly where the
 * hand-entered figure used to sit.
 *
 * ## Rows are discovered from the leads, keyed on (campaign, adset name)
 *
 * The attribution is already stamped on every paid lead, so a separate master
 * would only be a second thing to keep in sync.
 *
 * The key is deliberately NOT `adsetId`, which is absent from most leads (110 of
 * 400 in August, because Meta's export only began carrying it partway through) -
 * matching on it would compute cost per lead over a fraction of the leads. Nor
 * is it the adset name alone, which recurs under two campaigns as two separately
 * funded adsets. The PAIR is exact.
 *
 * The trade-off stays explicit: an adset that spent money and produced no leads
 * cannot be discovered and will not appear. `orphan` marks the reverse case
 * (spend saved, leads since gone) so recorded money is never silently dropped.
 */
export const MarketingAdsetRowSchema = z.object({
  /** Adset name as it appears in Meta and on the lead. Half of the identity key. */
  adsetName: z.string(),
  /** Campaign the adset sits under. The other half, and the roll-up group. */
  campaignName: z.string(),
  /** Meta's opaque adset id when any lead carried one. Reference only. */
  adsetId: z.string().nullable(),
  /** Destination master id the adset's leads resolve to. Null when unresolved. */
  destinationId: z.string().nullable(),
  destinationName: z.string(),
  /** Leads attributed to this adset in the selected months. */
  leads: z.number().int().nonnegative(),
  /**
   * Month-to-date spend as read off Meta - what the input box shows and what a
   * re-save REPLACES. Null when nothing has been entered yet. Never accumulated
   * across entries; only summed across adsets.
   *
   * Over a multi-month selection this is the sum of the months' figures, since
   * each month holds its own month-to-date total.
   */
  amount: z.number().nonnegative().nullable(),
  /** The IST day (YYYY-MM-DD) the figure was read off Meta. Null when unsaved. */
  asOfDate: IsoDateOnly.nullable(),
  /** amount / leads. Null while either is missing - the CPL the notes ask for. */
  costPerLead: z.number().nullable(),
  /**
   * True when the row exists only because spend was saved against an adset with
   * no leads in the window. Kept visible (and still counted) rather than hidden,
   * so the month total always explains itself.
   */
  orphan: z.boolean(),
});
export type MarketingAdsetRow = z.infer<typeof MarketingAdsetRowSchema>;

/** A campaign with its adsets nested - adsets partition their campaign exactly. */
export const MarketingCampaignSpendSchema = z.object({
  /** Campaign name - the grouping key. `campaign_id` is absent from virtually
   *  every lead in practice, so the NAME is what campaigns are keyed on here. */
  campaignName: z.string(),
  /** Every adset under this campaign targets the same destination. */
  destinationName: z.string(),
  leads: z.number().int().nonnegative(),
  /** Sum of the adsets' entered amounts. Derived, never entered. */
  amount: z.number().nonnegative(),
  /** How many of this campaign's adsets still have no amount entered. */
  missing: z.number().int().nonnegative(),
  costPerLead: z.number().nullable(),
  adsets: z.array(MarketingAdsetRowSchema),
});
export type MarketingCampaignSpend = z.infer<typeof MarketingCampaignSpendSchema>;

/** One destination's measured spend - what unlocks the destination cost columns. */
export const MarketingDestinationSpendSchema = z.object({
  /** Destination master id, or `unspecified`. */
  key: z.string(),
  name: z.string(),
  leads: z.number().int().nonnegative(),
  amount: z.number().nonnegative(),
  costPerLead: z.number().nullable(),
});
export type MarketingDestinationSpend = z.infer<typeof MarketingDestinationSpendSchema>;

/**
 * Per-category slice of a month (International / Domestic / uncategorised).
 * `conversionPct` and `avgOrderValue` are null when the denominator is zero -
 * the source sheet shows `#DIV/0!` there; the UI renders an em dash.
 */
export const MarketingCategorySliceSchema = z.object({
  /** Leads created in the month with this category. */
  query: z.number().int().nonnegative(),
  /** Leads WON in the month (keyed on `closedAt`) with this category. */
  converted: z.number().int().nonnegative(),
  /** converted / query, percent. Null when query is 0. */
  conversionPct: z.number().nullable(),
  /** Sum of the lead's own `ttvAmount` (Tour Total Value, pre-tax) over the won leads. */
  ttvAmount: z.number().nonnegative().nullable(),
  /** ttvAmount / converted. Null when nothing converted. */
  avgOrderValue: z.number().nullable(),
});
export type MarketingCategorySlice = z.infer<typeof MarketingCategorySliceSchema>;

/** One "how soon will they book" bucket: raw count plus its share. */
export const MarketingTimelineBucketSchema = z.object({
  count: z.number().int().nonnegative(),
  /** Share of the queries that DID answer. Null when none answered. */
  pct: z.number().nullable(),
});
export type MarketingTimelineBucket = z.infer<typeof MarketingTimelineBucketSchema>;

/**
 * Queries bucketed by the lead's "How soon will customer book?" answer
 * (`bookingTimeline`). The five form options collapse into the sheet's three
 * buckets:
 *
 *   0 to 30         <- 0-7 Days + 08-15 Days + 16-30 Days
 *   30 to 60        <- 1-3 Months
 *   greater than 60 <- 3+ Months
 *
 * Percentages are over the queries that actually answered (`specified`), which
 * is why they sum to ~100 in the source sheet. `unspecified` is reported
 * alongside so the coverage of the field stays visible rather than silently
 * deflating every bucket.
 */
export const MarketingPaymentTimelineSchema = z.object({
  /** Queries with a `bookingTimeline` set - the percentage denominator. */
  specified: z.number().int().nonnegative(),
  /** Queries with no answer captured. */
  unspecified: z.number().int().nonnegative(),
  within30Days: MarketingTimelineBucketSchema,
  thirtyToSixtyDays: MarketingTimelineBucketSchema,
  overSixtyDays: MarketingTimelineBucketSchema,
});
export type MarketingPaymentTimeline = z.infer<typeof MarketingPaymentTimelineSchema>;

/**
 * One month column of the "Query Profit and Spend" table.
 *
 * Basis, matching the source sheet:
 *   - Query     - leads CREATED in the month (all of them; marketing owns lead
 *                 generation, so this is not scoped to an assigned agent).
 *   - Converted - the SAME leads that went WON before their creation month
 *                 was out. A lead created in one month and won in the next is
 *                 in no Converted column - one month's spend is put against
 *                 what that month's queries returned inside the month. This is
 *                 deliberately not the closed-month rule the dashboards use.
 */
export const MarketingProfitMonthSchema = z.object({
  month: IsoMonth,
  label: z.string(),

  // ----- Cost side -----
  /** Resolved from the carry-forward rate history. Null when no rate covers the month. */
  managementCost: z.number().nonnegative().nullable(),
  /** Entered per month. Null until saved. */
  allAccountSpend: z.number().nonnegative().nullable(),
  /**
   * Every INCLUDED cost head resolved for this month, in master order - one
   * Spend row each. `managementCost` above is the `management` head's amount,
   * kept for callers that predate the master.
   */
  costs: z.array(MarketingResolvedCostSchema),
  /**
   * allAccountSpend + every resolved head. Null when ad spend is missing or a
   * REQUIRED head has no figure - a profit against a partial cost side would be
   * wrong rather than incomplete.
   */
  totalSpend: z.number().nullable(),
  /**
   * "Expected Spend of the Month". Mirrors `allAccountSpend` - it feeds no
   * other figure in the sheet (Pure Profit uses Total Spend), so it is derived
   * rather than stored. Becomes its own input if a forecast is ever needed.
   */
  expectedSpend: z.number().nonnegative().nullable(),

  // ----- Profit side -----
  /**
   * "Profit (Sales Tracker)" - GROSS margin booked in the month: the sum of
   * `netMarkupPaise` on the itineraries the month's won leads converted on.
   * Markup only; GST / TCS are pass-through and are not margin.
   */
  salesProfit: z.number().nullable(),
  /** "Pure Profit" = salesProfit - totalSpend. Null while totalSpend is unknown. */
  pureProfit: z.number().nullable(),

  // ----- Volume + conversion -----
  overall: MarketingCategorySliceSchema,
  international: MarketingCategorySliceSchema,
  domestic: MarketingCategorySliceSchema,
  /**
   * Leads created in the month with no `leadCategory` set. Reported explicitly
   * so International + Domestic + uncategorised always reconciles to Query
   * instead of silently under-counting.
   */
  uncategorisedQuery: z.number().int().nonnegative(),

  /** Mean days from lead creation to `closedAt` over the month's won leads. */
  avgDaysToConversion: z.number().nullable(),

  /**
   * "Query to Contactiblity Ratio" - percent of the month's queries that were
   * ever contacted (`firstContactedAt` set). Null when there were no queries.
   */
  contactabilityRatio: z.number().nullable(),
  /**
   * "Query to Active Ratio" - percent of the month's queries that ever reached
   * the `active` status, read from stage history rather than the current status
   * so a lead that has since moved on (won / lost) still counts. Null when
   * there were no queries.
   */
  activeRatio: z.number().nullable(),
  /**
   * "Query to Warm Ratio" - percent of the month's queries that ever reached
   * the `warm` sub-status (of Active), read from stage history like
   * `activeRatio`. Null when there were no queries.
   */
  warmRatio: z.number().nullable().default(null),
  /** Raw numerator behind `contactabilityRatio` - the month's queries ever contacted. */
  contactedCount: z.number().int().nonnegative(),
  /** Raw numerator behind `activeRatio` - the month's queries that ever reached `active`. */
  activeCount: z.number().int().nonnegative(),
  /** Raw numerator behind `warmRatio` - the month's queries that ever reached `warm`. */
  warmCount: z.number().int().nonnegative().default(0),

  /**
   * Efficiency ratios. All null while `totalSpend` is unknown (no cost captured
   * for the month) or the denominator is zero.
   *
   * Note ROI and RoAS differ by exactly 1 by construction: pureProfit is
   * salesProfit - totalSpend, so pureProfit/totalSpend = salesProfit/totalSpend - 1.
   * Both are kept because the source sheet reports both.
   */
  /**
   * "0 to 30" / "30 to 60" / "greater than 60" - the month's queries bucketed by
   * the lead's "How soon will customer book?" answer (`bookingTimeline`), NOT by
   * any elapsed-time calculation.
   */
  paymentTimeline: MarketingPaymentTimelineSchema,

  /** "CPQ" - cost per query: totalSpend / query. */
  costPerQuery: z.number().nullable(),
  /**
   * "CPQ at Meta Level" - ad spend alone over the month's queries, with no
   * retainer or overhead in the numerator: allAccountSpend / query.
   */
  cpqAtMetaLevel: z.number().nullable(),
  /** "ROI" - pureProfit / totalSpend, as a multiple (not a percent). */
  roi: z.number().nullable(),
  /** "RoAs" - salesProfit / totalSpend, as a multiple. */
  roas: z.number().nullable(),
  /** "CAC" - customer acquisition cost: totalSpend / converted. */
  cac: z.number().nullable(),
});
export type MarketingProfitMonth = z.infer<typeof MarketingProfitMonthSchema>;

/** One month's figures for a single channel (or sub-channel). */
export const MarketingChannelMonthSchema = z.object({
  month: IsoMonth,
  /** Gross markup booked from this channel's leads won in the month. */
  salesProfit: z.number().nullable(),
  overall: MarketingCategorySliceSchema,
  international: MarketingCategorySliceSchema,
  domestic: MarketingCategorySliceSchema,
});
export type MarketingChannelMonth = z.infer<typeof MarketingChannelMonthSchema>;

/** A sub-source, e.g. "Instagram Form" under the "Instagram" source. */
export const MarketingSubChannelSchema = z.object({
  key: z.string(),
  name: z.string(),
  months: z.array(MarketingChannelMonthSchema),
});
export type MarketingSubChannel = z.infer<typeof MarketingSubChannelSchema>;

/**
 * A lead source ("Google", "Instagram", ...) with its sub-sources nested.
 *
 * The parent's figures are the WHOLE source including every sub-source, so a
 * child never double-counts against its parent - it is a slice of it.
 */
export const MarketingChannelSchema = z.object({
  /** QuerySource id, or `unattributed` for leads with no source set. */
  key: z.string(),
  name: z.string(),
  months: z.array(MarketingChannelMonthSchema),
  children: z.array(MarketingSubChannelSchema),
});
export type MarketingChannel = z.infer<typeof MarketingChannelSchema>;

export const MarketingChannelResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  /** Column order, newest month first - matches the profit table. */
  months: z.array(z.object({ month: IsoMonth, label: z.string() })),
  /** Sorted by the newest month's query volume, busiest channel first. */
  channels: z.array(MarketingChannelSchema),
  generatedAt: IsoDateTimeSchema,
});
export type MarketingChannelResponse = z.infer<typeof MarketingChannelResponseSchema>;

/** One month's standing for a status (or sub-status). */
export const MarketingStatusMonthSchema = z.object({
  month: IsoMonth,
  count: z.number().int().nonnegative(),
  /** Share of the month's queries sitting here. Null when the month had none. */
  pct: z.number().nullable(),
});
export type MarketingStatusMonth = z.infer<typeof MarketingStatusMonthSchema>;

export const MarketingSubStatusSchema = z.object({
  key: z.string(),
  name: z.string(),
  months: z.array(MarketingStatusMonthSchema),
});
export type MarketingSubStatus = z.infer<typeof MarketingSubStatusSchema>;

/**
 * A lead status with its sub-statuses nested. As with channels, the parent
 * covers the WHOLE status including every sub-status - a child is a slice of
 * its parent, not an addition to it.
 */
export const MarketingStatusSchema = z.object({
  key: z.string(),
  /** Stable QueryStatus code (e.g. `active`) so the UI can key off it by identity, not label. */
  code: z.string().nullable(),
  name: z.string(),
  /** OPEN / WON / LOST, so the UI can tint by outcome. Null on unset. */
  bucket: z.string().nullable(),
  months: z.array(MarketingStatusMonthSchema),
  children: z.array(MarketingSubStatusSchema),
});
export type MarketingStatus = z.infer<typeof MarketingStatusSchema>;

export const MarketingLeadStatusResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  /** Column order, newest month first. */
  months: z.array(z.object({ month: IsoMonth, label: z.string() })),
  /** Total queries per month - the denominator behind every `pct`. */
  totals: z.array(MarketingStatusMonthSchema),
  statuses: z.array(MarketingStatusSchema),
  generatedAt: IsoDateTimeSchema,
});
export type MarketingLeadStatusResponse = z.infer<typeof MarketingLeadStatusResponseSchema>;

/**
 * One row of a contactability table: how many queries came in, how many were
 * lost, and what share were ever contacted.
 */
export const ContactabilityRowSchema = z.object({
  key: z.string(),
  name: z.string(),
  queries: z.number().int().nonnegative(),
  lost: z.number().int().nonnegative(),
  /** Leads with `firstContactedAt` set. */
  contacted: z.number().int().nonnegative(),
  /** contacted / queries, percent. Null when there were no queries. */
  contactedPct: z.number().nullable(),
  /**
   * Leads that ever reached `active` (read from stage history, so a lead that
   * has since moved on to won/lost still counts). Populated on the campaign
   * contactability report; omitted on the region/agent/destination tables,
   * which don't surface the active funnel.
   */
  active: z.number().int().nonnegative().optional(),
  /** active / queries, percent. Null when there were no queries. */
  activePct: z.number().nullable().optional(),
  /** active / contacted, percent. Null when nothing was contacted. */
  contactToActivePct: z.number().nullable().optional(),
  /**
   * Leads whose CURRENT status is `not_qualified` (sitting in NQ now).
   * Populated on the region/agent contactability tables; omitted elsewhere.
   */
  notQualified: z.number().int().nonnegative().optional(),
  /** notQualified / queries, percent. Null when there were no queries. */
  notQualifiedPct: z.number().nullable().optional(),
  /**
   * Of the `active` leads, how many had a customer WhatsApp REPLY (an inbound
   * whatsapp_messages row) on or before going active - the "WhatsApp drove this
   * active" count. Populated on the campaign contactability report alongside
   * `active`; omitted elsewhere. Nests inside `active`.
   */
  whatsAppActive: z.number().int().nonnegative().optional(),
  /** whatsAppActive / active, percent. Null when nothing reached active. */
  whatsAppToActivePct: z.number().nullable().optional(),
  /**
   * Leads currently WON (`current_bucket = 'WON'`) - which campaign the
   * conversions came from. Populated on the campaign contactability report;
   * omitted elsewhere.
   */
  won: z.number().int().nonnegative().optional(),
  /** won / queries, percent. Null when there were no queries. */
  wonPct: z.number().nullable().optional(),
});
export type ContactabilityRow = z.infer<typeof ContactabilityRowSchema>;

/** A channel with its agents nested - agents partition the channel exactly. */
export const AgentContactabilitySchema = ContactabilityRowSchema.extend({
  agents: z.array(ContactabilityRowSchema),
});
export type AgentContactability = z.infer<typeof AgentContactabilitySchema>;

/**
 * "Region wise" + "Agent wise" contactability. Both cover the WHOLE selected
 * range (not per month), matching the single-period tables in the source sheet.
 */
export const MarketingContactabilityResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  /** Departure-city grouping: Delhi NCR / Other / Not specified. */
  regions: z.array(ContactabilityRowSchema),
  regionTotal: ContactabilityRowSchema,
  /** Lead source, with the agents working it nested underneath. */
  channels: z.array(AgentContactabilitySchema),
  /**
   * Grand total across channels. Can be LOWER than `regionTotal` when some
   * leads have no assigned agent - those appear in an "Unassigned" agent row
   * rather than being dropped, so the two totals stay explainable.
   */
  channelTotal: ContactabilityRowSchema,
  generatedAt: IsoDateTimeSchema,
});
export type MarketingContactabilityResponse = z.infer<typeof MarketingContactabilityResponseSchema>;

/**
 * A channel with the ad campaigns that fed it nested underneath. Campaigns
 * partition their channel exactly: leads from the source with no campaign
 * captured (organic, offline, pre-tracking) land in a "Not set" row rather
 * than being dropped, so the children always reconcile to the parent.
 */
export const CampaignContactabilitySchema = ContactabilityRowSchema.extend({
  campaigns: z.array(ContactabilityRowSchema),
});
export type CampaignContactability = z.infer<typeof CampaignContactabilitySchema>;

/**
 * "Campaign wise Contactibility" - its own report, deliberately NOT folded into
 * the region/agent tables: it keys on the Meta ad `campaignId` captured on the
 * lead, which only paid channels carry, so mixing it into a table that must
 * cover every lead would leave most rows empty.
 *
 * Single-period over the WHOLE selected range, like its sibling contactability
 * tables. Lost % is derived by the UI from `lost / queries` rather than stored,
 * so there is one rounding rule for it in one place.
 */
export const MarketingCampaignContactabilityResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  /** Lead source, busiest first, with its campaigns nested. */
  channels: z.array(CampaignContactabilitySchema),
  /** Grand total across every channel - covers all leads in range. */
  total: ContactabilityRowSchema,
  generatedAt: IsoDateTimeSchema,
});
export type MarketingCampaignContactabilityResponse = z.infer<
  typeof MarketingCampaignContactabilityResponseSchema
>;

/**
 * One cell block of "Month Wise Active Status": the five funnel columns from
 * the source sheet plus their sum.
 *
 * `other` is everything that reached none of the five (still uncontacted, not
 * qualified, ...). It is NOT part of `total` - the sheet's Grand Total is the
 * five columns only - but it is returned so nothing is silently dropped.
 */
export const ActiveStatusCellSchema = z.object({
  /** LOST bucket. */
  lost: z.number().int().nonnegative(),
  /** active -> proposal. */
  proposal: z.number().int().nonnegative(),
  /** active -> warm. */
  warm: z.number().int().nonnegative(),
  /** active -> hot. */
  hot: z.number().int().nonnegative(),
  /** WON bucket. */
  won: z.number().int().nonnegative(),
  /** Sum of the five columns above - the sheet's Grand Total. */
  total: z.number().int().nonnegative(),
  /** Leads in none of the five. Excluded from `total`; surfaced for reconciliation. */
  other: z.number().int().nonnegative(),
});
export type ActiveStatusCell = z.infer<typeof ActiveStatusCellSchema>;

export const ActiveStatusMonthSchema = ActiveStatusCellSchema.extend({
  month: IsoMonth,
  label: z.string(),
});
export type ActiveStatusMonth = z.infer<typeof ActiveStatusMonthSchema>;

/** A channel with a row per month beneath it. */
export const ActiveStatusChannelSchema = z.object({
  key: z.string(),
  name: z.string(),
  /** The channel's own totals across every month in range. */
  totals: ActiveStatusCellSchema,
  months: z.array(ActiveStatusMonthSchema),
});
export type ActiveStatusChannel = z.infer<typeof ActiveStatusChannelSchema>;

/**
 * "Month Wise Active Status" - channel -> month, against the funnel columns.
 * Months are the lead's CREATION month, matching Query everywhere else.
 */
export const MarketingActiveStatusResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  channels: z.array(ActiveStatusChannelSchema),
  grandTotal: ActiveStatusCellSchema,
  generatedAt: IsoDateTimeSchema,
});
export type MarketingActiveStatusResponse = z.infer<typeof MarketingActiveStatusResponseSchema>;

/**
 * One destination's performance over the selected range. Single-period (not
 * per month) because destinations are a wide list - a month-by-month matrix
 * would be unreadable, and "which destinations sell" is the question here.
 */
export const MarketingDestinationRowSchema = z.object({
  /** Destination master id, or `unspecified` when the lead has none. */
  key: z.string(),
  name: z.string(),
  /** Gross markup booked from this destination's won leads. */
  salesProfit: z.number().nullable(),
  overall: MarketingCategorySliceSchema,

  /**
   * Ad spend measured for this destination: the sum of the adset rows whose
   * leads resolve here, apportioned to the window by day count.
   *
   * These five fields were previously absent and rendered as em dashes,
   * because spend existed only as ONE monthly total and splitting it pro-rata
   * would have handed every destination an identical Cost Per Query. Adset-level
   * capture makes them real - an adset targets a single destination, so this is
   * measured, not allocated. Still null when no adset spend has been entered for
   * the window, which is the honest answer rather than a zero.
   */
  spend: z.number().nonnegative().nullable(),
  /** spend / query. Null when spend is unknown or the destination had no queries. */
  costPerQuery: z.number().nullable(),
  /** salesProfit - spend. Null while spend is unknown. */
  profitAfterSpend: z.number().nullable(),
  /** profitAfterSpend / spend, as a multiple. Null on unknown or zero spend. */
  roi: z.number().nullable(),
  /** salesProfit / spend, as a multiple. */
  roas: z.number().nullable(),
});
export type MarketingDestinationRow = z.infer<typeof MarketingDestinationRowSchema>;

/** A channel with its destinations nested - destinations partition the channel. */
export const DestinationContactabilitySchema = ContactabilityRowSchema.extend({
  destinations: z.array(ContactabilityRowSchema),
});
export type DestinationContactability = z.infer<typeof DestinationContactabilitySchema>;

/**
 * One destination's "How soon will customer book?" spread. Counts only leads
 * that answered the field, so the row total is the answered count - not the
 * destination's whole query volume.
 */
export const DestinationTimelineRowSchema = z.object({
  key: z.string(),
  name: z.string(),
  within30Days: z.number().int().nonnegative(),
  thirtyToSixtyDays: z.number().int().nonnegative(),
  overSixtyDays: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
export type DestinationTimelineRow = z.infer<typeof DestinationTimelineRowSchema>;

/** One row of the lost-reason pivot. `cells` aligns index-for-index with `columns`. */
export const LostReasonPivotRowSchema = z.object({
  key: z.string(),
  name: z.string(),
  cells: z.array(z.number().int().nonnegative()),
  total: z.number().int().nonnegative(),
});
export type LostReasonPivotRow = z.infer<typeof LostReasonPivotRowSchema>;

export const LostReasonPivotChannelSchema = LostReasonPivotRowSchema.extend({
  destinations: z.array(LostReasonPivotRowSchema),
});
export type LostReasonPivotChannel = z.infer<typeof LostReasonPivotChannelSchema>;

/**
 * "Destination wise Lost Region" - LOST leads only, pivoted channel ->
 * destination against the lost reason recorded on the lead.
 */
export const DestinationLostPivotSchema = z.object({
  /** Reason labels, busiest first; "(blank)" covers leads lost with no reason. */
  columns: z.array(z.string()),
  channels: z.array(LostReasonPivotChannelSchema),
  total: LostReasonPivotRowSchema,
});
export type DestinationLostPivot = z.infer<typeof DestinationLostPivotSchema>;

export const MarketingDestinationResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  /** Busiest destination first. */
  destinations: z.array(MarketingDestinationRowSchema),
  total: MarketingDestinationRowSchema,
  /** "Destination Wise Contactibility" - channel, then destinations beneath. */
  contactability: z.array(DestinationContactabilitySchema),
  contactabilityTotal: ContactabilityRowSchema,
  /** Booking-timeline spread per destination, alphabetical like the sheet. */
  timeline: z.array(DestinationTimelineRowSchema),
  /** Totals plus the share each bucket holds - reuses the Overview shape. */
  timelineTotal: MarketingPaymentTimelineSchema,
  /** "Destination wise Lost Region" - lost leads by reason. */
  lostReasons: DestinationLostPivotSchema,
  generatedAt: IsoDateTimeSchema,
});
export type MarketingDestinationResponse = z.infer<typeof MarketingDestinationResponseSchema>;

/**
 * One lead source's block of the sheet: the same rows as the overall table,
 * computed over that source's leads only.
 *
 * Its cost side is what is MEASURED against the channel - ad spend from the
 * adsets whose leads came in on it, plus cost heads bound to it - never a
 * share of an overhead. So the channel blocks do not sum to the overall block
 * on the cost rows, and that is the point: WhatsApp or a retainer that serves
 * every channel is only ever counted once, in Overall.
 *
 * A channel block has no completeness gate of its own. A channel with no cost
 * recorded at all shows "-" on the cost-derived rows rather than locking.
 */
export const MarketingProfitChannelSchema = z.object({
  /** QuerySource id, or `unattributed` for leads with no source set. */
  key: z.string(),
  name: z.string(),
  /** Newest month first, matching `months` on the response. */
  months: z.array(MarketingProfitMonthSchema),
});
export type MarketingProfitChannel = z.infer<typeof MarketingProfitChannelSchema>;

export const MarketingProfitResponseSchema = z.object({
  from: IsoDateOnly,
  to: IsoDateOnly,
  /** Newest month first, so the table reads like the source sheet. */
  months: z.array(MarketingProfitMonthSchema),
  /**
   * The same table per lead source, busiest first. Includes a source that had
   * no leads but has a cost head bound to it, so recorded money always shows.
   */
  channels: z.array(MarketingProfitChannelSchema),
  generatedAt: IsoDateTimeSchema,
});
export type MarketingProfitResponse = z.infer<typeof MarketingProfitResponseSchema>;

/**
 * Marketing / day sheet: every seller in the round-robin down the side, every
 * destination with at least one seller across the top, and in each cell the
 * seller's marketing leads/day for that destination (the user form's
 * "Marketing / day" box) with how many the round-robin has dealt them today
 * (IST). A mapped destination with nothing set is the gap the sheet shows.
 */
export const MarketingDailyCapCellSchema = z.object({
  /** Null = mapped with no marketing / day set. */
  cap: z.number().int().nullable(),
  /** Fresh leads the round-robin dealt for this destination today. */
  dealtToday: z.number().int(),
});
export type MarketingDailyCapCell = z.infer<typeof MarketingDailyCapCellSchema>;

export const MarketingDailyCapAgentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** False = employable but switched out of the round-robin on the user form. */
  autoAssign: z.boolean(),
  /** Keyed by destination id; a destination missing here is not mapped. */
  cells: z.record(z.string(), MarketingDailyCapCellSchema),
});
export type MarketingDailyCapAgent = z.infer<typeof MarketingDailyCapAgentSchema>;

export const MarketingDailyCapResponseSchema = z.object({
  /** The IST day the dealt counts belong to. */
  day: IsoDateOnly,
  /** International first, then domestic; alphabetical within each. */
  destinations: z.array(
    z.object({ id: z.string().uuid(), name: z.string(), isInternational: z.boolean() }),
  ),
  /** Alphabetical. */
  agents: z.array(MarketingDailyCapAgentSchema),
  generatedAt: IsoDateTimeSchema,
});
export type MarketingDailyCapResponse = z.infer<typeof MarketingDailyCapResponseSchema>;
